import React from 'react';
import { Link } from 'react-router-dom';

export type AdminMetric = {
  key?: string;
  label: string;
  value: number | string | null | undefined;
  previous_value?: number | null;
  change_absolute?: number | null;
  change_percent?: number | null;
  unit?: 'count' | 'currency' | 'percent' | 'duration' | string;
  currency?: string | null;
  definition?: string | null;
  drilldown?: string | null;
  unavailable?: boolean;
};

function formatValue(m: AdminMetric): string {
  if (m.unavailable) return 'Unavailable';
  if (m.value === null || m.value === undefined) return '—';
  if (typeof m.value === 'string') return m.value;
  if (m.unit === 'percent') return `${m.value}%`;
  if (m.unit === 'currency' && m.currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: m.currency,
        maximumFractionDigits: 2,
      }).format(m.value / 100);
    } catch {
      return `${(m.value / 100).toFixed(2)} ${m.currency}`;
    }
  }
  return new Intl.NumberFormat().format(m.value);
}

export const AdminMetricCard: React.FC<{ metric: AdminMetric }> = ({ metric }) => {
  const change = metric.change_percent;
  const abs = metric.change_absolute;
  const showChange = change !== null && change !== undefined;
  const up = (abs ?? 0) > 0;
  const down = (abs ?? 0) < 0;
  const changeLabel = showChange
    ? `${up ? '+' : ''}${change}%`
    : abs !== null && abs !== undefined && metric.previous_value === 0 && Number(metric.value) > 0
      ? 'New'
      : null;

  const body = (
    <div
      className={`surface rounded-lg p-4 h-full border border-white/[0.06] ${
        metric.drilldown ? 'hover:border-orange-500/30 transition-colors cursor-pointer' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="text-sm font-medium text-gray-400">{metric.label}</h3>
        {metric.definition ? (
          <span
            className="text-gray-500 text-xs"
            title={metric.definition}
            aria-label={metric.definition}
          >
            ?
          </span>
        ) : null}
      </div>
      <p
        className={`text-2xl font-semibold tracking-tight ${
          metric.unavailable ? 'text-gray-500' : 'text-white'
        }`}
      >
        {formatValue(metric)}
      </p>
      {changeLabel ? (
        <p
          className={`mt-1 text-xs font-medium ${
            up ? 'text-emerald-400' : down ? 'text-rose-300' : 'text-gray-400'
          }`}
        >
          <span aria-hidden="true">{up ? '▲ ' : down ? '▼ ' : ''}</span>
          <span>
            {changeLabel}
            {showChange ? ' vs prior period' : ''}
          </span>
        </p>
      ) : (
        <p className="mt-1 text-xs text-gray-600">—</p>
      )}
    </div>
  );

  if (metric.drilldown) {
    return (
      <Link to={metric.drilldown} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 rounded-lg">
        {body}
      </Link>
    );
  }
  return body;
};

export default AdminMetricCard;
